/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2024 Hexio a.s. <contact@hexio.io> (hexio.io)
 * @license Apache 2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useState } from "react";
import { Toolbar } from "../Toolbar/Toolbar";
import { CodeEditor } from "@adapptio/lib-editor-ui";
import { ToolbarButton } from "../Toolbar/ToolbarButton";
import { ToolbarSpacer } from "../Toolbar/ToolbarSpacer";
import { ToolbarNote } from "../Toolbar/ToolbarNote";

export interface IBlueprintEditorProps {
	initialValue: string;
	onApplyChanges: (value: string) => void;
}

export const BlueprintEditor: React.FC<IBlueprintEditorProps> = ({
	initialValue,
	onApplyChanges
}) => {
	const [ canEdit, setCanEdit ] = useState(false);
	const [ areChangesApplies, setAreChangesApplied ] = useState(false);
	const [ currentValue, setCurrentValue ] = useState(initialValue);

	const handleEnableEdit = () => {
		setCurrentValue(initialValue);
		setCanEdit(true);
		setAreChangesApplied(false);
	};

	const handleApplyChangesAndSync = () => {
		onApplyChanges(currentValue);
		setCanEdit(false);
		setAreChangesApplied(true);
	}

	const handleApplyChanges = () => {
		onApplyChanges(currentValue);
		setAreChangesApplied(true);
	}

	const handleDiscardChanges = () => {
		setCurrentValue(initialValue);
		setCanEdit(false);
	}

	const handleValueChange = (value: string) => {
		setCurrentValue(value);
		setAreChangesApplied(false);
	}

	useEffect(() => {
		if (initialValue !== currentValue && !canEdit) {
			setCurrentValue(initialValue);
		}
	}, [ initialValue, currentValue, canEdit ]);

	return (
		<>
			<Toolbar>
				{!canEdit && (
					<>
						<ToolbarSpacer grow={1} />
						<ToolbarButton
							label="Edit Blueprint"
							icon="mdi/pencil"
							onClick={handleEnableEdit}
						/>
					</>
				)}
				{canEdit && (
					<>
						<ToolbarNote>Warning: All changes outside blueprint editor are not reflected until your changes are either discarded or applied to avoid loss of your edits.</ToolbarNote>
						<ToolbarSpacer grow={1} />
						<ToolbarButton
							style="primary"
							label="Apply & Sync"
							icon="mdi/content-save"
							onClick={handleApplyChangesAndSync}
						/>
						<ToolbarButton
							label="Apply Changes"
							icon="mdi/eye"
							onClick={handleApplyChanges}
						/>
						<ToolbarButton
							label="Discard"
							icon="mdi/close"
							onClick={handleDiscardChanges}
							disabled={areChangesApplies}
						/>
					</>
				)}
			</Toolbar>
			<CodeEditor
				language="yaml"
				readOnly={!canEdit}
				value={currentValue}
				onChange={handleValueChange}
			/>
		</>
	)
};
